import { editable, posterType } from '@/src/tools/contentModulesFieldName';
import { EPreviewSize } from '@/src/types/common/SalesChannel';
import {
  EModuleWidth,
  EResponsivePosterGap,
  EResponsivePosterType,
  EResponsiveResizingOption,
  EnumModuleType,
} from '@/src/types/common/module';
import { get } from 'lodash';
import { checkResponsivePoster } from 'src/constrains/SalesChannelContent-Revamp/ModuleList/validator';
import {
  animationFields,
  moduleStyleFields,
} from 'src/strategies/render/constant';
import {
  gapSelect,
  images as iK,
  moduleWidthSelect,
  resizingOptionsSelect,
} from 'src/tools/contentModulesFieldName';
import i18n from '../i18n';
import { ResponsiveContainer, ResponsiveField } from '../util';
import { ANIMATION, IMAGES, MOUDLE_STYLE, POSTER } from './constant';
import {
  animation_not_editable,
  getAnimationTemplate,
  getImagesTemplate,
  getModuleStyleTemplate,
} from './imageCarousel';

const getSettingTemplate = (values: any, responsiveType: EPreviewSize) => {
  const v =
    responsiveType === EPreviewSize.WIDE || values[`${POSTER}_${editable}`];
  let commonFields: Array<any> = [
    {
      field: ResponsiveField.PosterType,
      disabled: !v,
      options: [
        {
          label: i18n['general.postertype.one'],
          value: EResponsivePosterType.ONE,
        },
        {
          label: i18n['general.postertype.twoinone1'],
          value: EResponsivePosterType.TWO_1_1,
        },
        {
          label: i18n['general.postertype.twoinone2'],
          value: EResponsivePosterType.TWO_1_2,
        },
        {
          label: i18n['general.postertype.twoinone3'],
          value: EResponsivePosterType.TWO_2_1,
        },
        {
          label: i18n['general.postertype.three'],
          value: EResponsivePosterType.THREE,
        },
        {
          label: i18n['general.postertype.four'],
          value: EResponsivePosterType.FOUR,
        },
        {
          label: i18n['general.postertype.five'],
          value: EResponsivePosterType.FIVE,
        },
        {
          label: i18n['general.postertype.six'],
          value: EResponsivePosterType.SIX,
        },
      ],
    },
    {
      field: ResponsiveField.ModuleWidth,
      disabled: !v,
      options: getModuleWidthOptionsByType(responsiveType),
    },
  ];
  if (values[posterType] !== EResponsivePosterType.ONE) {
    commonFields.push({
      field: ResponsiveField.Gap,
      disabled: !v,
      options: getGapOptionsByType(responsiveType),
    });
  }

  commonFields = [
    ...commonFields,
    {
      field: ResponsiveField.ResizingOption,
      disabled: !v,
      options: [
        {
          label: i18n['general.content.fixed'],
          value: EResponsiveResizingOption.FIXED,
        },
        {
          label: i18n['general.content.scalable'],
          value: EResponsiveResizingOption.SCALABLE,
        },
      ],
    },
  ];

  return commonFields;
};

export const not_editable = [...animation_not_editable];

export default (values: any, responsiveType: EPreviewSize) => {
  const posterSetting = getSettingTemplate(values, responsiveType);
  const moduleStyleSetting = getModuleStyleTemplate(
    values,
    responsiveType,
    EnumModuleType.ResponsivePoster,
  );
  const animation = getAnimationTemplate(values, responsiveType);
  const images = getImagesTemplate(values, responsiveType);

  return [
    {
      title: i18n['general.content.poster'],
      id: POSTER,
      field: posterSetting,
      fields: [
        [posterType],
        [moduleWidthSelect],
        [resizingOptionsSelect],
        [gapSelect],
      ],
    },
    {
      title: i18n['general.content.moduleStyle'],
      id: MOUDLE_STYLE,
      type: ResponsiveContainer.ModuleStyle,
      field: moduleStyleSetting,
      fields: moduleStyleFields,
    },
    {
      title: i18n['general.content.animation'],
      id: ANIMATION,
      field: animation,
      fields: animationFields,
    },
    {
      title: i18n['general.content.images'],
      id: IMAGES,
      field: images,
      moduleType: EnumModuleType.ResponsiveImageCarousel,
      headerErrorDependencies: [iK], //for section validation
      fields: [[iK]],
      validator: (value, extra) => {
        const i = get(value, 'images');
        const { checkImages } = checkResponsivePoster();
        return checkImages(i, extra);
      },
    },
  ];
};

const getModuleWidthOptionsByType = (responsiveType: EPreviewSize) => {
  switch (responsiveType) {
    case EPreviewSize.MOBILE: {
      return [
        { label: i18n['general.content.fullWidth'], value: EModuleWidth.FULL },
        { label: i18n['general.content.wideWidth'], value: EModuleWidth.WIDE },
      ];
    }
    case EPreviewSize.TABLET: {
      return [
        { label: i18n['general.content.fullWidth'], value: EModuleWidth.FULL },
        { label: i18n['general.content.wideWidth'], value: EModuleWidth.WIDE },
      ];
    }
    case EPreviewSize.WIDE: {
      return [
        { label: i18n['general.content.fullWidth'], value: EModuleWidth.FULL },
        { label: i18n['general.content.wideWidth'], value: EModuleWidth.WIDE },
        { label: i18n['general.content.none'], value: EModuleWidth.NONE },
      ];
    }
    default:
      return [];
  }
};

const getGapOptionsByType = (responsiveType: EPreviewSize) => {
  switch (responsiveType) {
    case EPreviewSize.MOBILE: {
      return [
        {
          label: i18n['general.postergap.none'],
          value: EResponsivePosterGap.NONE,
        },
        {
          label: i18n['general.postergap.small'],
          value: EResponsivePosterGap.SMALL,
        },
        {
          label: i18n['general.postergap.middle'],
          value: EResponsivePosterGap.MIDDLE,
        },
      ];
    }
    case EPreviewSize.TABLET: {
      return [
        {
          label: i18n['general.postergap.none'],
          value: EResponsivePosterGap.NONE,
        },
        {
          label: i18n['general.postergap.small'],
          value: EResponsivePosterGap.SMALL,
        },
        {
          label: i18n['general.postergap.middle'],
          value: EResponsivePosterGap.MIDDLE,
        },
        {
          label: i18n['general.postergap.large'],
          value: EResponsivePosterGap.LARGE,
        },
      ];
    }
    case EPreviewSize.WIDE: {
      return [
        {
          label: i18n['general.postergap.none'],
          value: EResponsivePosterGap.NONE,
        },
        {
          label: i18n['general.postergap.small'],
          value: EResponsivePosterGap.SMALL,
        },
        {
          label: i18n['general.postergap.middle'],
          value: EResponsivePosterGap.MIDDLE,
        },
        {
          label: i18n['general.postergap.large'],
          value: EResponsivePosterGap.LARGE,
        },
      ];
    }
    default:
      return [];
  }
};
